import { useState } from 'react'
import {
  Bot,
  Cable,
  Check,
  FileStack,
  GitBranch,
  HelpCircle,
  KeyRound,
  Share2,
  type LucideIcon,
  MessageSquare,
  MoreHorizontal,
  Siren
} from 'lucide-react'
import { cn } from '../../lib/utils'
import { formatRelativeTime } from '../../lib/time'
import { Button } from '../ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../ui/dropdown-menu'
import type {
  NotificationAction,
  NotificationKind,
  PresentedNotification
} from './notification-presentation'

const KIND_ICON: Record<NotificationKind, LucideIcon> = {
  agent_finding: Bot,
  agent_run_failed: Bot,
  agent_run_completed: Bot,
  rule_fired: Siren,
  rule_cleared: Siren,
  rule_test: Siren,
  plc_change_review: GitBranch,
  ingest_failed: FileStack,
  ingest_done: FileStack,
  ingest_question: HelpCircle,
  ingest_paused: FileStack,
  ingest_review: FileStack,
  diff_failed: FileStack,
  diff_applied: FileStack,
  mtc_disconnected: Cable,
  mtc_sequence_gap: Cable,
  mtc_model_changed: Cable,
  connection: Cable,
  access_request: KeyRound,
  access_decided: KeyRound,
  resource_shared: Share2,
  legacy: Siren
}

const INLINE_ACTIONS = 3

export interface NotificationItemProps {
  item: PresentedNotification
  onOpen: (item: PresentedNotification) => void
  onAction: (item: PresentedNotification, action: NotificationAction) => Promise<void> | void
  density?: 'popover' | 'page'
  /** Hide actions (Done tab). */
  readOnly?: boolean
}

/**
 * One notification in the bell or on the page: kind + source, a title that
 * opens the subject, a context line, and the producer's inline actions.
 */
export function NotificationItem({
  item,
  onOpen,
  onAction,
  density = 'popover',
  readOnly
}: NotificationItemProps) {
  const { notification, members, presentation } = item
  const [busy, setBusy] = useState<string | null>(null)
  const Icon = KIND_ICON[presentation.kind] ?? Siren
  const unread = notification.read === false
  // The server folds repeats into one row and counts them; older rows are
  // folded client-side.
  const occurrences = Math.max(notification.occurrences ?? 1, members.length)
  const severity = notification.severity ?? 'info'
  const isAgent = presentation.kind.startsWith('agent_')
  const inline = presentation.actions.slice(0, INLINE_ACTIONS)
  const overflow = presentation.actions.slice(INLINE_ACTIONS)

  const runAction = async (action: NotificationAction) => {
    setBusy(action.label)
    try {
      await onAction(item, action)
    } finally {
      setBusy(null)
    }
  }

  return (
    <div
      data-notification-id={notification.id}
      data-kind={presentation.kind}
      className={cn(
        'group/notif relative flex gap-2.5 text-left',
        density === 'popover' ? 'px-3 py-2.5' : 'px-4 py-3',
        presentation.bucket === 'action' &&
          'before:absolute before:inset-y-2.5 before:left-0 before:w-0.75 before:rounded-r',
        presentation.bucket === 'action' && severity === 'critical' && 'before:bg-st-down',
        presentation.bucket === 'action' && severity === 'warning' && 'before:bg-st-warn'
      )}
    >
      <span
        className={cn(
          'mt-0.5 flex size-6.5 shrink-0 items-center justify-center rounded-md bg-bg-sunk text-ink-2',
          isAgent && presentation.bucket === 'action' && 'bg-st-info-bg text-st-info',
          !isAgent &&
            presentation.bucket === 'action' &&
            severity === 'critical' &&
            'bg-st-down-bg text-st-down',
          !isAgent &&
            presentation.bucket === 'action' &&
            severity === 'warning' &&
            'bg-st-warn-bg text-st-elev'
        )}
        aria-hidden="true"
      >
        <Icon className="size-3.5" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-1.5 text-11 text-ink-3">
          <span className="font-medium text-ink-2">{presentation.kindLabel}</span>
          {presentation.source ? (
            <>
              <span aria-hidden="true">·</span>
              <span className="min-w-0 truncate">{presentation.source}</span>
            </>
          ) : null}
          {occurrences > 1 ? (
            <span className="rounded border border-line bg-bg-sunk px-1 font-mono text-10 text-ink-2">
              {occurrences}×
            </span>
          ) : null}
          {notification.for_you ? (
            <span className="rounded bg-st-info-bg px-1 text-10 font-medium text-st-info">
              For you
            </span>
          ) : null}
          <span className="ml-auto flex shrink-0 items-center gap-1.5 tabular-nums">
            {formatRelativeTime(notification.last_occurred_at ?? notification.created_at)}
            {unread ? (
              <span
                data-testid="notification-unread"
                aria-label="Unread"
                className="size-1.5 rounded-full bg-st-info"
              />
            ) : null}
          </span>
        </div>
        {presentation.href ? (
          <button
            type="button"
            onClick={() => onOpen(item)}
            className="cursor-pointer text-left text-13 font-medium leading-snug text-ink hover:underline hover:underline-offset-2 focus-visible:underline focus-visible:outline-none"
          >
            {presentation.title}
          </button>
        ) : (
          <span className="text-13 font-medium leading-snug text-ink">{presentation.title}</span>
        )}
        {presentation.context ? (
          <span className="text-12 text-ink-2">{presentation.context}</span>
        ) : null}
        {!readOnly && presentation.actions.length > 0 ? (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {inline.map((action) => (
              <Button
                key={action.label}
                type="button"
                size="xs"
                variant={action.primary ? 'default' : 'outline'}
                disabled={busy !== null}
                onClick={() => void runAction(action)}
              >
                {action.kind === 'discuss' ? <MessageSquare /> : null}
                {action.kind === 'mutation' && action.mutation === 'resolve_agent_run' ? (
                  <Check />
                ) : null}
                {busy === action.label ? 'Working…' : action.label}
              </Button>
            ))}
            {overflow.length > 0 ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" size="icon-xs" variant="ghost" aria-label="More actions">
                    <MoreHorizontal />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {overflow.map((action) => (
                    <DropdownMenuItem key={action.label} onSelect={() => void runAction(action)}>
                      {action.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}
